/**
 * Narrow study session (below the 768px cut `useIsMobile` already uses).
 * Exists so `DefaultLayout` does not gain a third flag (`hideNav` and
 * `hideFilter` are the only ones). `TestClock` and `useNotifications` are
 * absent here on purpose.
 */
import { X } from "lucide-react";
import type { KeyboardEvent, ReactNode, Ref } from "react";

export interface SessaoFullscreenProps {
  /** Subject of the current card. Empty when unknown or when not studying. */
  assunto: string;
  /** 1-based position from the session hook. Pass through. Do not derive it. */
  posicao: number;
  total: number;
  /** True only while the session phase is `estudando`. Gates the title's position and the progress bar. */
  estudando: boolean;
  onFechar: () => void;
  /** Deck, empty state, failure, loading, conclusion. Scrolls between header and footer. */
  children: ReactNode;
  /** Thumb-reach controls. Omit when there is nothing to flip or rate. */
  controles?: ReactNode;
  /**
   * Right-hand header action — the admin-mode switch. The in-session edit
   * entry itself lives in the card's own header (`editSlot`), not here.
   * Omit it and a spacer of the same size keeps the title centered.
   */
  acaoDireita?: ReactNode;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  focoRef?: Ref<HTMLDivElement>;
  /** Extra classes on the shell. The study view uses this to pause pointer events while the editor is open. */
  className?: string;
  /** "Marcação N de M" from `rotuloMarcacaoDoCartao`. Omit when it returns null. */
  marcacao?: string | null;
}

/** Narrow header title. Studying: "Primeiros Socorros · 12 de 40". No subject: "12 de 40". Not studying: "". */
export function tituloSessaoEstreita(
  assunto: string,
  posicao: number,
  total: number,
  estudando: boolean,
): string {
  if (!estudando) return "";
  const posicaoFrase = `${posicao} de ${total}`;
  const assuntoLimpo = assunto.trim();
  if (assuntoLimpo.length > 0) return `${assuntoLimpo} · ${posicaoFrase}`;
  return posicaoFrase;
}

export function SessaoFullscreen({
  assunto,
  posicao,
  total,
  estudando,
  onFechar,
  children,
  controles,
  acaoDireita,
  onKeyDown,
  focoRef,
  className = '',
  marcacao,
}: SessaoFullscreenProps): JSX.Element {
  const titulo = tituloSessaoEstreita(assunto, posicao, total, estudando);
  const assuntoLimpo = assunto.trim();
  const posicaoFrase = `${posicao} de ${total}`;
  const largura = total > 0 ? (posicao / total) * 100 : 0;
  const marcacaoVisivel = typeof marcacao === "string" && marcacao !== "";
  const rotuloBarra = titulo || posicaoFrase;

  return (
    <div
      data-sessao-tela="cheia"
      ref={focoRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={`flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden bg-canvas outline-none focus-visible:ring-2 focus-visible:ring-select/[0.42] ${className}`}
    >
      <header className="shrink-0 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            data-sessao-fechar=""
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink hover:bg-surface-hover"
          >
            <X size={20} aria-hidden />
          </button>
          <h1
            data-sessao-titulo=""
            className="flex min-w-0 flex-1 items-baseline justify-center gap-1.5 text-center text-sm font-semibold text-ink"
          >
            {estudando && assuntoLimpo ? (
              <span className="min-w-0 truncate">{assuntoLimpo}</span>
            ) : null}
            {estudando && assuntoLimpo ? <span className="shrink-0">·</span> : null}
            {estudando ? (
              <span data-sessao-posicao="" className="shrink-0 tabular-nums">
                {posicaoFrase}
              </span>
            ) : null}
          </h1>
          {acaoDireita ?? <span aria-hidden className="h-11 w-11 shrink-0" />}
        </div>
        {estudando && marcacaoVisivel ? (
          <p
            data-marcacao
            className="mt-1 text-center text-xs font-medium tabular-nums text-ink-secondary"
          >
            {marcacao}
          </p>
        ) : null}
        {estudando ? (
          <div
            className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-surface-muted"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={posicao}
            aria-label={marcacaoVisivel ? `${marcacao}. ${rotuloBarra}` : rotuloBarra}
            data-sessao-progresso=""
          >
            <div className="h-full rounded-full bg-rate-easy" style={{ width: `${largura}%` }} />
          </div>
        ) : null}
      </header>
      <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      {controles != null ? (
        <footer
          data-sessao-rodape=""
          className="shrink-0 border-t border-edge bg-canvas px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          {controles}
        </footer>
      ) : null}
    </div>
  );
}
